/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { requestBaseURL } from '@/config/serviceAPI';
import { getAxios } from '@/lib/axios';
import { type YmmeVehicleResponse, type YmmeAcesVehicleResponse } from '../interface';
import { createQuery } from '@/utils/createReactQuery';
import { type AxiosInstance } from 'axios';
import { useAcesFlags } from '../utils/acesFlags';
import { getKey as getVehiclesKey } from '../api/getVehicles';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { dispatchEvent } from '@/utils/eventPublisher';
import { useGlobalState } from '@/hooks/useGlobalState';
import { getHeaderDataFromCache, HEADER_PRIMARY_KEY } from '@/features/header/api/getHeader';
import { getPreferredVehicle } from '@/features/header/utils/getPreferredVehicle';
import { setPreferredVehicleCookies } from '@/utils/analytics/dataLayer/preferredVehicleCookies';

const URL = `${requestBaseURL}/ecomm/b2c/v2/currentUser/vehicles/vehicleDecoder`;

type VehicleByVinQueryOptions = {
  vinNumber: string | undefined;
  acesTransitionPhase: boolean;
};

const { useData: useAcesVehicleVINLookup } = createQuery<
  YmmeAcesVehicleResponse,
  VehicleByVinQueryOptions
>('vin', async (options, axiosInstance) => getAcesVehicleByVIN(options, axiosInstance));

const getAcesVehicleByVIN = async (
  options: VehicleByVinQueryOptions,
  axiosInstance?: AxiosInstance
) => {
  const { vinNumber, acesTransitionPhase } = options;

  if (!vinNumber) {
    throw new Error('vinNumber is required');
  }

  const response = await getAxios(axiosInstance).get<YmmeAcesVehicleResponse>(`${URL}`, {
    params: {
      vin: vinNumber,
      transition: acesTransitionPhase,
    },
  });

  return response.data;
};

const onVehiclesSuccess = async (queryClient: QueryClient, data: YmmeVehicleResponse) => {
  queryClient.setQueryData(getVehiclesKey(), data);

  await queryClient.refetchQueries([HEADER_PRIMARY_KEY]);

  const headerData = getHeaderDataFromCache(queryClient);
  const preferredVehicle = getPreferredVehicle(headerData);

  if (preferredVehicle) {
    setPreferredVehicleCookies({
      vehicleYear: preferredVehicle.year,
      vehicleMake: preferredVehicle.make,
      vehicleModel: preferredVehicle.model,
      vehicleEngine: preferredVehicle.engine,
      vehicleId: preferredVehicle.catalogVehicleId,
    });
  }
};

type UseVINLookupOptions = {
  vinNumber?: string;
  onSuccess?: () => void;
  enabled?: boolean;
};

export const useAcesVINLookup = ({
  vinNumber,
  onSuccess,
  enabled = false,
}: UseVINLookupOptions) => {
  const { acesTransitionPhase } = useAcesFlags();
  const queryClient = useQueryClient();
  const [, setSearchState] = useGlobalState('search');
  return useAcesVehicleVINLookup({
    enabled: Boolean(vinNumber) && enabled,
    onSuccess: (data: YmmeVehicleResponse) => {
      setSearchState({
        preferredVehicleChanged: true,
      });
      void onVehiclesSuccess(queryClient, data);
      onSuccess?.();
      dispatchEvent('preferredVehicleUpdated', { vehicleSet: true });
    },
    vinNumber,
    acesTransitionPhase,
    cacheTime: 0,
  });
};
